import React from 'react';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {AppText, AppCaption, AppCardTitle} from '../../../Presentation/components/ui/Typography';
import {Package, MapPin, Eye, Trash2, Hash} from 'lucide-react';
import DateTimeDisplay from '../../../Presentation/components/ui/DateTimeDisplay';
import {BaseTableProps} from '../../../types/table.types';
import {useNotification} from '../../../Services/useNotification';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';
import {
  INVENTORY_LOADING_TEXT,
  inventoryEmptyText
} from '../ui/inventoryUi';

// نموذج السيريال الموحد
export interface SerialItem {
  id: string;
  serialNumber: string;
  productName: string;
  productId: string;
  locationName: string;
  locationId: string;
  status: 'available' | 'assigned' | 'sold';
  statusText: string;
  createdAt: string;
  assignedTo?: string;
  lastMovement?: string;
}

interface SerialsTrackingTableProps extends BaseTableProps<SerialItem> {
  onDeleteSerial?: (id: string) => Promise<boolean>;
  onViewDetails?: (serial: SerialItem) => void;
  onTrackSerial?: (serial: SerialItem) => void;
  deletingSerialId?: string | null;
  exportable?: boolean;
  loading?: boolean;
}

const SerialsTrackingTable: React.FC<SerialsTrackingTableProps> = ({
  data: serials,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onDeleteSerial,
  onViewDetails,
  onTrackSerial,
  deletingSerialId,
  showPageSizeSelector = true,
  showPaginationInfo = true,
  exportable = false,
  loading = false
}) => {
  const {error: showError} = useNotification();
  // Export configuration
  const {createConfig} = useExportConfig<SerialItem>();

  const exportConfig = createConfig([
    {key: 'serialNumber', title: 'الرقم التسلسلي'},
    {key: 'productName', title: 'اسم المنتج'},
    {key: 'productId', title: 'معرف المنتج'},
    {key: 'locationName', title: 'اسم الموقع'},
    {key: 'locationId', title: 'معرف الموقع'},
    {key: 'status', title: 'الحالة', formatter: ExportFormatters.serialStatus},
    {key: 'createdAt', title: 'تاريخ الإنشاء', formatter: ExportFormatters.dateTime},
    {key: 'assignedTo', title: 'مخصص لـ'},
    {
      key: 'lastMovement',
      title: 'آخر حركة',
      formatter: (value) => {
        if (value && typeof value === 'string') {
          return ExportFormatters.dateTime(value);
        }
        return 'لا يوجد';
      }
    }
  ], 'سيريالات', 'تصدير سجلات السيريالات', 'تصدير جميع سجلات السيريالات مع التفاصيل الكاملة');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: serials,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  const columns: TableColumn<SerialItem>[] = [
    {
      key: 'serialNumber',
      title: <AppTableHeaderCell label="الرقم التسلسلي" />,
      render: (serial: SerialItem) => (
        <AppText className="font-mono bg-gray-50 px-2 py-1 rounded font-bold" dir="ltr">
          {serial.serialNumber}
        </AppText>
      )
    },
    {
      key: 'productName',
      title: <AppTableHeaderCell label="المنتج" />,
      render: (serial: SerialItem) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
            <Package className="w-4 h-4 text-brand-600" />
          </div>
          <div>
            <AppText className="font-medium">
              {serial.productName || `منتج ${serial.productId}` || 'منتج غير محدد'}
            </AppText>
            {!serial.productName && (
              <AppCaption className="text-gray-500">
                (اسم المنتج غير متاح)
              </AppCaption>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'locationName',
      title: <AppTableHeaderCell label="الموقع" />,
      render: (serial: SerialItem) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-green-50 to-green-100 rounded-lg flex items-center justify-center">
            <MapPin className="w-4 h-4 text-green-600" />
          </div>
          <AppText>{serial.locationName}</AppText>
        </div>
      )
    },
    {
      key: 'status',
      title: <AppTableHeaderCell label="الحالة" />,
      render: (serial: SerialItem) => {
        const statusConfig = {
          available: {label: 'متاح', className: 'bg-green-100 text-green-800'},
          assigned: {label: 'مخصص للمسوق', className: 'bg-brand-100 text-brand-800'},
          sold: {label: 'مباع', className: 'bg-purple-100 text-purple-800'}
        };

        const config = statusConfig[serial.status] || {label: serial.statusText, className: 'bg-gray-100 text-gray-800'};

        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
        );
      }
    },

    {
      key: 'createdAt',
      title: <AppTableHeaderCell label="تاريخ الإنشاء" />,
      render: (serial: SerialItem) => (
        <DateTimeDisplay
          dateString={serial.createdAt}
          dateClassName="text-sm text-gray-600"
          timeClassName="text-xs text-gray-500"
        />
      )
    }
  ];

  return (
    <div className={`flex-1 min-h-[calc(100vh-80px)] flex flex-col ${exportable ? 'p-6' : ''}`}>
      {exportable && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-brand-500" />
            <AppCardTitle>الأرقام التسلسلية</AppCardTitle>
          </div>
          <ExportButton
            onExport={handleExport}
            isExporting={isExporting}
            canExport={canExport}
            recordCount={recordCount}
            showDropdown={true}
          />
        </div>
      )}

      <div className={exportable ? 'mt-6 overflow-x-auto' : ''}>
      <AppTable
        columns={columns}
        data={serials}
        rowKey={(serial, index) => `serial-${serial.id}-${index || 0}`}
        emptyText={inventoryEmptyText('أرقام تسلسلية')}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showPageSizeSelector={showPageSizeSelector}
        showPaginationInfo={showPaginationInfo}
        loading={loading}
        loadingRows={pageSize || 10}
        loadingMessage={INVENTORY_LOADING_TEXT}
        actions={(serial: SerialItem) => {
          const actions = [];

          // إضافة عرض التفاصيل إذا كان متاحاً (يفتح المودال)
          if (onViewDetails) {
            actions.push({
              label: 'عرض التفاصيل',
              icon: <Eye className="text-brand-600" size={20} />,
              onClick: () => onViewDetails(serial)
            });
          }

          // إضافة عرض في حركة المخزون إذا كان متاحاً (يطابق Sales)
          if (onTrackSerial) {
            actions.push({
              label: 'عرض في حركة المخزون',
              icon: <MapPin className="text-green-600" size={20} />,
              onClick: () => onTrackSerial(serial)
            });
          }

          // إضافة حذف السيريال إذا كان متاحاً (فقط للمستودع الرئيسي)
          if (onDeleteSerial && (serial.locationId === '0' || serial.locationName === 'المستودع الرئيسي')) {
            actions.push({
              label: deletingSerialId === serial.id ? 'جاري الحذف...' : 'حذف السيريال',
              icon: <Trash2 className="text-red-600" size={20} />,
              onClick: async () => {
                try {
                  await onDeleteSerial(serial.id);
                  // Success notification is handled by the parent component
                } catch (error) {
                  // Error notification is handled by the parent component
                  // Only show error here if parent doesn't handle it
                  if (error instanceof Error && error.message) {
                    showError(error.message);
                  }
                }
              },
              danger: true
            });
          }

          return actions;
        }}
      />
      </div>
    </div>
  );
};

export default SerialsTrackingTable;
